/**
 * Platform Configuration
 *
 * Centralized configuration for OAuth platform integrations.
 * Used by PlatformSlot component for display and connection logic.
 */

import type { Platform } from '@/types/connection.types'

/**
 * Platform configuration interface
 */
export interface PlatformConfig {
  /** Internal platform identifier */
  name: string

  /** Display name for UI */
  displayName: string

  /** Brand color for icons and UI elements */
  color: string

  /** Short description for tooltips/help text */
  description: string

  /** Production platform value */
  platform: Platform

  /** Sandbox platform value (if available) */
  sandboxPlatform: Platform
}

/**
 * All supported platforms
 * Extracted from ConnectPlatformModal for reuse in PlatformSlot
 */
export const PLATFORM_CONFIGS: Record<string, PlatformConfig> = {
  square: {
    name: 'square',
    displayName: 'Square',
    color: '#3E4348',
    description: 'Point of sale and payment platform',
    platform: 'square',
    sandboxPlatform: 'square-sandbox',
  },
  quickbooks: {
    name: 'quickbooks',
    displayName: 'QuickBooks',
    color: '#2CA01C',
    description: 'Accounting and invoicing software',
    platform: 'quickbooks',
    sandboxPlatform: 'quickbooks-sandbox',
  },
  shopify: {
    name: 'shopify',
    displayName: 'Shopify',
    color: '#96BF48',
    description: 'E-commerce platform',
    platform: 'shopify',
    sandboxPlatform: 'shopify-sandbox',
  },
}

/**
 * Get platform config by platform name
 */
export function getPlatformConfig(platformName: string): PlatformConfig | undefined {
  return PLATFORM_CONFIGS[platformName]
}

/**
 * Get platform config by Platform value (handles sandbox suffix)
 */
export function getPlatformConfigByValue(platform: Platform): PlatformConfig | undefined {
  const basePlatform = platform.replace('-sandbox', '') as string
  return PLATFORM_CONFIGS[basePlatform]
}

/**
 * Get all platform configs as array (for iteration)
 */
export function getAllPlatformConfigs(): PlatformConfig[] {
  return Object.values(PLATFORM_CONFIGS)
}
